/**
 * StudySync Fitbit Reauthorization Component
 * Handles Fitbit token refresh and reauthorization for existing participants
 */

import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Alert,
  TextField,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  FitnessCenter,
  Refresh,
  Warning,
  CheckCircle,
  Info,
  Person,
} from '@mui/icons-material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FitbitService } from '../../services/fitbitService';
import { ParticipantService } from '../../services/participantService';
import LoadingSpinner from '../common/LoadingSpinner';
import { analyticsService } from '../../services/analyticsService';

const FitbitReauthorization: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [participantId, setParticipantId] = useState('');
  const [email, setEmail] = useState('');

  // Get participant info from URL params if available
  useEffect(() => {
    const urlParticipantId = searchParams.get('participantId');
    const urlEmail = searchParams.get('email');
    
    if (urlParticipantId) setParticipantId(urlParticipantId);
    if (urlEmail) setEmail(urlEmail);
  }, [searchParams]);

  const handleReauthorization = async () => {
    if (!participantId.trim()) {
      setError('Please enter your Participant ID');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      // First verify the participant exists and get their data
      if (!email.trim()) {
        setError('Email is required for verification.');
        return;
      }

      const verifyResponse = await ParticipantService.verifyParticipant(
        participantId.trim(),
        email.trim().toLowerCase()
      );

      if (!verifyResponse.success || !verifyResponse.data) {
        setError('Participant verification failed. Please check your information.');
        return;
      }

      const studyCode = verifyResponse.data.studyCode;

      // Initiate Fitbit OAuth flow
      analyticsService.trackParticipantFitbitReauthInitiated({ participantId: participantId.trim() });
      await FitbitService.initiateFitbitOAuth(studyCode);
      analyticsService.trackParticipantFitbitReauthorized({ success: true });
      setSuccess('Fitbit reauthorization successful! Your device is now reconnected.');

    } catch (err: any) {
      setError('Reauthorization failed: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleManualReauth = async () => {
    if (!participantId.trim() || !email.trim()) {
      setError('Please enter both Participant ID and email address');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Verify participant and get study info
      const verifyResponse = await ParticipantService.verifyParticipant(
        participantId.trim(),
        email.trim().toLowerCase()
      );

      if (!verifyResponse.success || !verifyResponse.data) {
        setError('Participant verification failed. Please check your information.');
        return;
      }

      const studyCode = verifyResponse.data.studyCode;

      // Initiate Fitbit OAuth
      analyticsService.trackParticipantFitbitReauthInitiated({ participantId: participantId.trim() });
      await FitbitService.initiateFitbitOAuth(studyCode);
      analyticsService.trackParticipantFitbitReauthorized({ success: true });
      setSuccess('Fitbit reauthorization initiated! Please complete the authorization process.');

    } catch (err: any) {
      setError('Failed to initiate reauthorization: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Processing Fitbit reauthorization..." />;
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <FitnessCenter sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
            Fitbit Reauthorization
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Reconnect your Fitbit device to continue study participation
          </Typography>
        </Box>

        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Paper elevation={3} sx={{ p: 4 }}>
          {/* Information Card */}
          <Card sx={{ mb: 4, backgroundColor: 'info.light', color: 'info.contrastText' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Info sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Why Reauthorization is Needed
                </Typography>
              </Box>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle sx={{ color: 'info.contrastText' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Token Expiration"
                    secondary="Fitbit access tokens expire periodically for security"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle sx={{ color: 'info.contrastText' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Continued Data Collection"
                    secondary="Reauthorization ensures uninterrupted study participation"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle sx={{ color: 'info.contrastText' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Security & Privacy"
                    secondary="Regular reauthorization maintains data security standards"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>

          {/* Quick Reauth (if participant ID in URL) */}
          {participantId && !email && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Quick Reauthorization
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Your participant information has been detected. Click below to reconnect your Fitbit device.
              </Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={<Refresh />}
                onClick={handleReauthorization}
                disabled={loading}
                fullWidth
              >
                {loading ? 'Reconnecting...' : 'Reconnect Fitbit Device'}
              </Button>
            </Box>
          )}

          {/* Manual Reauth Form */}
          {(!participantId || email) && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Manual Reauthorization
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Enter your participant information to reconnect your Fitbit device.
              </Typography>

              <TextField
                fullWidth
                label="Participant ID"
                value={participantId}
                onChange={(e) => setParticipantId(e.target.value)}
                placeholder="P1234567890ABCDEF"
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: <Person sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />

              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: <Person sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />

              <Button
                variant="contained"
                size="large"
                startIcon={<Refresh />}
                onClick={handleManualReauth}
                disabled={loading || !participantId.trim() || !email.trim()}
                fullWidth
              >
                {loading ? 'Processing...' : 'Reconnect Fitbit Device'}
              </Button>
            </Box>
          )}

          {/* Help Section */}
          <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Need Help?
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <Warning color="warning" />
                </ListItemIcon>
                <ListItemText
                  primary="Can't find your Participant ID?"
                  secondary="Check your enrollment confirmation email or contact the research team"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Info color="info" />
                </ListItemIcon>
                <ListItemText
                  primary="Authorization not working?"
                  secondary="Make sure you're using the same Fitbit account as your original enrollment"
                />
              </ListItem>
            </List>

            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/participant/verify')}
              >
                Go to Participant Verification
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default FitbitReauthorization;
